/**
 * MarketplacePage — public catalog of installable workflows.
 *
 * Mounted at /projects/:projectId/marketplace (project-scoped so the
 * sidebar context + Deploy target are unambiguous). When the user
 * clicks "Deploy" inside the detail modal we POST to
 * /marketplace/workflows/:slug/deploy with the current projectId →
 * toast on success + close the modal.
 *
 * Layout mirrors the OpenAI GPT marketplace:
 *   - Title + subtitle
 *   - "Trending" section with a two-column numbered list
 *   - Each card = number + icon + name + tagline (2-line truncate) + "By <author>"
 *
 * The detail modal opens on click and shows the full row: icon, title,
 * by-author, description, capabilities, conversation starters,
 * ratings histogram, and a fixed-bottom Deploy button labelled
 * "Deploy to <project name>".
 */

import React, { useEffect, useState, useCallback, useMemo } from 'react';
import { useLocation } from 'react-router-dom';
import { useParams, useOutletContext, useNavigate } from 'react-router-dom';
import { useQueryClient } from '@tanstack/react-query';
import { Helmet } from 'react-helmet-async';
import { queryKeys } from '../../hooks/queryKeys';
import { IonIcon } from '@ionic/react';
import {
  star,
  searchOutline,
  closeCircle,
  chevronForwardOutline,
  rocketOutline,
  checkmarkCircle,
} from 'ionicons/icons';
import Marketplace from '../../services/marketplace';
import { useMarketplace } from '../../hooks/useMarketplace';
import AuthService from '../../services/auth';
import ProjectsService from '../../services/projects';
import MarketplaceDetailModal from './MarketplaceDetailModal';
import MarketplaceDeployWizardModal from './MarketplaceDeployWizardModal';
import AgentModelModal from '../../components/molecules/AgentModelModal/AgentModelModal';
import { useAgentOptions, findAgentOptionValue } from '../../utils/agent-options';
import { isChatRuntime, COPILOT_LIMIT_MESSAGE } from '../../utils/chat-runtime';
import { MARKETPLACE_TAG_POOL, MARKETPLACE_TAG_SET } from './marketplace-tag-pool';
import PublicNav from '../../layouts/PublicNav';
import PublicFooter from '../../layouts/PublicFooter';
import AppsMarketplaceSection from '../AppsMarketplacePage/AppsMarketplaceSection';
import './MarketplacePage.css';

// Truncate a string to N lines worth of characters as a coarse
// approximation — the CSS line-clamp does the real visual cut. This
// is only here to prevent very-long taglines from breaking layout
// reflow before the clamp kicks in.
const MAX_TAGLINE = 180;

// Special-case display labels for short acronym-style tag values
// where naive Title Case looks wrong ("e2e" → "E2e" feels off; we
// want "E2E"). Add new entries here as the marketplace grows; missing
// keys fall through to the title-case algorithm in formatFacetLabel.
const FACET_LABEL_OVERRIDES = Object.freeze({
  e2e: 'E2E',
  sdk: 'SDK',
  api: 'API',
  ai: 'AI',
  ui: 'UI',
  cli: 'CLI',
  pr: 'PR',
});

/**
 * Render a facet value (category or tag) as a chip label.
 *
 *   'sentry'        → 'Sentry'
 *   'on-call'       → 'On Call'
 *   'pull-request'  → 'Pull Request'
 *   'Engineering'   → 'Engineering' (already cased, unchanged)
 *   'e2e'           → 'E2E' (override table)
 */
function formatFacetLabel(s) {
  if (!s) return '';
  const lower = String(s).toLowerCase();
  if (FACET_LABEL_OVERRIDES[lower]) return FACET_LABEL_OVERRIDES[lower];
  // Already mixed-case (e.g. 'Engineering') → leave it.
  if (s !== lower && s !== s.toUpperCase()) return s;
  return lower
    .split(/[-_\s]+/)
    .filter(Boolean)
    .map((w) => FACET_LABEL_OVERRIDES[w] || (w.charAt(0).toUpperCase() + w.slice(1)))
    .join(' ');
}

// Workflow card icon with a letter fallback. The marketplace icon URL is
// a remote CDN path (https://zibby.dev/marketplace-icons/<slug>.png); when
// it's absent or 404s (e.g. a freshly-synced template whose PNG isn't on
// the CDN yet) we render the first-letter chip instead of a broken image.
const WorkflowCardIcon = ({ icon, label }) => {
  const [failed, setFailed] = useState(false);
  const letter = (label || '?').slice(0, 1).toUpperCase();
  if (!icon || failed) {
    return (
      <span className="marketplace-card-icon marketplace-card-icon--fallback">{letter}</span>
    );
  }
  return (
    // eslint-disable-next-line jsx-a11y/img-redundant-alt
    <img
      src={icon}
      alt=""
      className="marketplace-card-icon"
      width={56}
      height={56}
      loading="lazy"
      decoding="async"
      onError={() => setFailed(true)}
    />
  );
};

// ── Featured hero helpers ──────────────────────────────────────────────
// Derive a stable brand gradient for a featured item. We don't have a brand
// color field in the catalog, so we hash the slug into a hue and build a
// tasteful two-stop gradient. Copilot (and other known slugs) get a
// hand-tuned premium palette so the flagship slot always looks deliberate.
const FEATURED_PALETTES = {
  'zibby-copilot': { from: '#4F46E5', to: '#7C3AED', accent: '#a78bfa' },
  'chat-ops-bot': { from: '#0ea5e9', to: '#6366f1', accent: '#7dd3fc' },
  // Sentry-brand purple — same premium look as Copilot, distinct hue.
  'sentry-triage': { from: '#6E56CF', to: '#362D59', accent: '#c4b5fd' },
  // GitHub graphite/dark-theme — recognizably GitHub, distinct from the
  // purples/indigos above.
  'github-code-review': { from: '#2D333B', to: '#0F1318', accent: '#9aa7b4' },
  // GitLab brand gradient — tanuki orange easing into GitLab purple.
  'gitlab-code-review': { from: '#FC6D26', to: '#8133D6', accent: '#ffc7a3' },
};

// Curated hero taglines for featured slides — overrides the catalog's short
// one-liner so each slide's description reads as a full TWO lines (matching
// Copilot, so the card text block is the same height across slides).
const FEATURED_TAGLINES = {
  'sentry-triage':
    'Watches your Sentry issues hourly — reproduces each one, finds the root cause, then ships an auto-fix and opens a PR for review.',
  'github-code-review':
    'Reviews every pull request the moment it opens — flags bugs, security issues, and risky changes, then posts inline comments your team can act on.',
  'gitlab-code-review':
    'Reviews every merge request automatically — catches bugs, security holes, and regressions, then leaves inline notes right where they matter.',
};

const hashHue = (str) => {
  let h = 0;
  for (let i = 0; i < (str || '').length; i += 1) {
    h = (h * 31 + str.charCodeAt(i)) % 360;
  }
  return h;
};

const getFeaturedPalette = (entry) => {
  const slug = entry?.slug || '';
  if (FEATURED_PALETTES[slug]) return FEATURED_PALETTES[slug];
  // Default: a tasteful indigo/blue base nudged by a slug-derived hue so
  // different featured items read as distinct without ever looking garish.
  const hue = hashHue(slug || entry?.name || 'zibby');
  // Bias toward the cool indigo/blue/violet band (200–280) for brand fit.
  const base = 200 + (hue % 80);
  return {
    from: `hsl(${base}, 70%, 42%)`,
    to: `hsl(${(base + 40) % 360}, 72%, 30%)`,
    accent: `hsl(${base}, 85%, 78%)`,
  };
};

// A small chat-style demo that "sells" the agent in the hero's right rail.
// Copilot gets a bespoke deploy-bot exchange (mirrors the Adobe "@App do X →
// result" pattern). Every other slug falls back to a generic prompt built
// from the item's own name so the hero never looks empty.
// Returns an ARRAY of exchanges; the Slack window auto-rotates through them.
const getFeaturedDemo = (entry) => {
  if (entry?.slug === 'zibby-copilot') {
    return [
      { prompt: '@Zibby fix my flaky test in CircleCI', reply: 'Root-caused + fixed — PR opened' },
      { prompt: '@Zibby fix the Sentry issue', reply: 'Triaged + patched — PR opened' },
    ];
  }
  if (entry?.slug === 'chat-ops-bot') {
    return [
      { prompt: '@Zibby restart the api service', reply: 'Service back up' },
      { prompt: '@Zibby deploy my app to prod', reply: 'Deployed in 40s' },
    ];
  }
  if (entry?.slug === 'sentry-triage') {
    return [
      { prompt: '@Zibby triage SENTRY-4821', reply: 'Root-caused + patched — PR opened' },
      { prompt: '@Zibby fix the prod 500 spike', reply: 'Triaged + fix shipped — PR opened' },
    ];
  }
  const name = entry?.name || entry?.slug || 'this agent';
  return [{ prompt: `@Zibby run ${name}`, reply: 'On it — done' }];
};

// Which featured slugs render the premium Slack-style chat window (vs. the
// simple generic two-bubble demo). The Slack mock is the flagship sell for the
// conversational copilots; anything else falls back so the hero never crashes
// on an unexpected slug.
const SLACK_DEMO_SLUGS = new Set(['zibby-copilot', 'chat-ops-bot']);

// A polished, product-screenshot-style Slack chat window for the hero's right
// rail. Decorative (aria-hidden on the wrapper) but each row still carries a
// descriptive aria-label so the whole exchange is announced once as a single
// scene. Reads like a real Slack DM/channel: a chrome header with a workspace
// title + traffic-light dots, a user message, then the Zibby bot reply with an
// "APP" badge (Slack's bot convention) and a deploy result, plus a couple of
// faint skeleton lines hinting at more conversation.
const FeaturedSlackWindow = ({ demos, palette }) => {
  const list = Array.isArray(demos) && demos.length ? demos : [{ prompt: '', reply: '' }];
  const [i, setI] = useState(0);
  useEffect(() => {
    if (list.length < 2) return undefined;
    const t = setInterval(() => setI((c) => (c + 1) % list.length), 8000);
    return () => clearInterval(t);
  }, [list.length]);
  const demo = list[i % list.length];
  return (
  <div
    className="mkt-slack"
    style={{ '--slack-accent': palette.accent }}
    role="img"
    aria-label={`Slack chat demo: a user types "${demo.prompt}" and the Zibby app replies "${demo.reply}".`}
  >
    <div className="mkt-slack-chrome" aria-hidden="true">
      <span className="mkt-slack-dots">
        <span className="mkt-slack-dot mkt-slack-dot--r" />
        <span className="mkt-slack-dot mkt-slack-dot--y" />
        <span className="mkt-slack-dot mkt-slack-dot--g" />
      </span>
      <span className="mkt-slack-channel">
        <span className="mkt-slack-channel-icon">#</span>
        zibby-ops
      </span>
    </div>
    <div className="mkt-slack-thread mkt-slack-thread--rotate" aria-hidden="true" key={i}>
      <div className="mkt-slack-msg">
        <span className="mkt-slack-avatar mkt-slack-avatar--user">Y</span>
        <span className="mkt-slack-body">
          <span className="mkt-slack-meta">
            <span className="mkt-slack-name">You</span>
            <span className="mkt-slack-time">9:41 AM</span>
          </span>
          <span className="mkt-slack-text">{demo.prompt}</span>
        </span>
      </div>
      <div className="mkt-slack-msg mkt-slack-msg--bot">
        <span className="mkt-slack-avatar mkt-slack-avatar--bot" aria-hidden="true">
          <IonIcon icon={rocketOutline} />
        </span>
        <span className="mkt-slack-body">
          <span className="mkt-slack-meta">
            <span className="mkt-slack-name">Zibby</span>
            <span className="mkt-slack-appbadge">APP</span>
            <span className="mkt-slack-time">9:41 AM</span>
          </span>
          <span className="mkt-slack-text mkt-slack-text--bot">
            <IonIcon icon={checkmarkCircle} className="mkt-slack-check" aria-hidden="true" />
            {demo.reply}
          </span>
          <span className="mkt-slack-skeleton">
            <span className="mkt-slack-skel-line" />
            <span className="mkt-slack-skel-line mkt-slack-skel-line--short" />
          </span>
        </span>
      </div>
    </div>
  </div>
  );
};

// Generic fallback demo (the original two-bubble mock) for any featured slug
// that isn't one of the chat copilots — keeps the hero populated without
// pretending an arbitrary agent lives in Slack.
const FeaturedGenericDemo = ({ demo }) => (
  <div className="mkt-hero-demo" aria-hidden="true">
    <div className="mkt-hero-bubble mkt-hero-bubble--user">{demo.prompt}</div>
    <div className="mkt-hero-bubble mkt-hero-bubble--bot">
      <IonIcon icon={checkmarkCircle} className="mkt-hero-bubble-check" />
      {demo.reply}
    </div>
  </div>
);

// The Sentry agent's hero shows a mini WORKFLOW GRAPH of its triage→fix
// pipeline instead of reusing the Slack window — on-brand (Zibby IS a
// workflow-graph platform) and more "interesting" per the design ask. A pulse
// travels the rail and the nodes stagger in; the terminal node is the green
// "PR opened" outcome. Framed like the Slack window for visual consistency.
const SENTRY_GRAPH_NODES = [
  { label: 'Sentry alert', sub: 'TypeError · prod' },
  { label: 'Reproduce & triage', sub: 'stack trace + context' },
  { label: 'Root cause found', sub: 'null deref in handler' },
  { label: 'Patch → PR opened', sub: '#482 — ready for review', done: true },
];

// Featured slugs that render the mini WORKFLOW-GRAPH demo (vs. the Slack
// window) — on-brand for Zibby (a workflow-graph platform). Each entry supplies
// the pipeline nodes + a chrome title + an a11y label. github/gitlab-code-review
// reuse the exact same component as sentry-triage, just different stages.
const FEATURED_GRAPHS = {
  'sentry-triage': {
    title: 'sentry-triage · run',
    label: 'Sentry triage workflow: alert, reproduce and triage, root cause found, then patch with a PR opened.',
    nodes: SENTRY_GRAPH_NODES,
  },
  'github-code-review': {
    title: 'github-code-review · run',
    label: 'GitHub code-review workflow: pull request opened, fetch the diff and context, review for bugs and security, then inline comments posted.',
    nodes: [
      { label: 'Pull request opened', sub: 'feat/checkout · +214 −38' },
      { label: 'Fetch diff & context', sub: 'changed files + history' },
      { label: 'Review', sub: 'bugs · security · style' },
      { label: 'Inline comments posted', sub: '6 findings · 1 blocker', done: true },
    ],
  },
  'gitlab-code-review': {
    title: 'gitlab-code-review · run',
    label: 'GitLab code-review workflow: merge request opened, fetch the diff and context, review for bugs and security, then inline notes posted.',
    nodes: [
      { label: 'Merge request opened', sub: 'feat/billing · +176 −22' },
      { label: 'Fetch diff & context', sub: 'changed files + history' },
      { label: 'Review', sub: 'bugs · security · style' },
      { label: 'Inline notes posted', sub: '5 findings · approved', done: true },
    ],
  },
};

const FeaturedGraphDemo = ({ palette, nodes = SENTRY_GRAPH_NODES, title = 'sentry-triage · run', label }) => {
  // A "playhead" that steps DOWN the pipeline, highlighting each node in turn
  // then looping back to the top — it walks through (reviews) all the steps and
  // repeats, per the design ask.
  const [active, setActive] = useState(0);
  useEffect(() => {
    const t = setInterval(
      () => setActive((c) => (c + 1) % nodes.length),
      1400,
    );
    return () => clearInterval(t);
  }, [nodes.length]);
  return (
    <div
      className="mkt-graph"
      style={{ '--graph-accent': palette.accent }}
      role="img"
      aria-label={label || `${title} workflow`}
    >
      <div className="mkt-graph-chrome" aria-hidden="true">
        <span className="mkt-slack-dots">
          <span className="mkt-slack-dot mkt-slack-dot--r" />
          <span className="mkt-slack-dot mkt-slack-dot--y" />
          <span className="mkt-slack-dot mkt-slack-dot--g" />
        </span>
        <span className="mkt-graph-title">{title}</span>
      </div>
      <div className="mkt-graph-body" aria-hidden="true">
        <span className="mkt-graph-rail"><span className="mkt-graph-pulse" /></span>
        {nodes.map((n, i) => (
          <div
            key={n.label}
            className={`mkt-graph-node${n.done ? ' mkt-graph-node--done' : ''}${i === active ? ' is-active' : ''}`}
            style={{ animationDelay: `${0.1 + i * 0.13}s` }}
          >
            <span className="mkt-graph-dot">
              {n.done ? <IonIcon icon={checkmarkCircle} /> : null}
            </span>
            <span className="mkt-graph-node-body">
              <span className="mkt-graph-node-label">{n.label}</span>
              <span className="mkt-graph-node-sub">{n.sub}</span>
            </span>
          </div>
        ))}
      </div>
    </div>
  );
};

// Full-width Adobe / App-Store-style hero carousel for the promoted
// "Featured" slot. Renders one premium hero per featured item with a
// brand gradient, large icon, CTA, and a chat-mockup demo. With >1 item it
// supports prev/next arrows, pagination dots, and gentle autoplay with
// pause-on-hover. The CTA reuses the exact same openDetail flow as a normal
// card so deploying a featured agent is identical to any other.
const FeaturedHeroCarousel = ({ items, onSelect }) => {
  const [index, setIndex] = useState(0);
  const [paused, setPaused] = useState(false);
  const count = items.length;
  const multi = count > 1;

  // Clamp index if the featured list shrinks underneath us.
  useEffect(() => {
    if (index > count - 1) setIndex(0);
  }, [count, index]);

  const go = useCallback(
    (next) => setIndex((cur) => ((next % count) + count) % count),
    [count],
  );
  const prev = useCallback(() => go(index - 1), [go, index]);
  const next = useCallback(() => go(index + 1), [go, index]);

  // Auto-rotate every 8s, paused on hover/focus.
  useEffect(() => {
    if (!multi || paused) return undefined;
    const t = setInterval(() => setIndex((cur) => (cur + 1) % count), 8000);
    return () => clearInterval(t);
  }, [multi, paused, count]);

  const onKeyDown = useCallback(
    (e) => {
      if (!multi) return;
      if (e.key === 'ArrowLeft') { prev(); }
      else if (e.key === 'ArrowRight') { next(); }
    },
    [multi, prev, next],
  );

  const entry = items[index] || items[0];
  const palette = getFeaturedPalette(entry);
  const demos = getFeaturedDemo(entry);
  const tagline = (FEATURED_TAGLINES[entry.slug] || entry.tagline || entry.description || '').slice(0, 170);

  return (
    <div className="mkt-group mkt-group--featured" id="mkt-group-__featured__">
      <div
        className="mkt-hero-carousel"
        role="group"
        aria-roledescription="carousel"
        aria-label="Featured agents"
        onMouseEnter={() => setPaused(true)}
        onMouseLeave={() => setPaused(false)}
        onFocus={() => setPaused(true)}
        onBlur={() => setPaused(false)}
        onKeyDown={onKeyDown}
      >
        <div
          key={index}
          className="mkt-hero"
          style={{
            '--hero-from': palette.from,
            '--hero-to': palette.to,
            '--hero-accent': palette.accent,
          }}
          aria-roledescription="slide"
          aria-label={`${entry.name || entry.slug}${multi ? ` (${index + 1} of ${count})` : ''}`}
        >
          <div className="mkt-hero-left">
            <span className="mkt-hero-icon-wrap">
              <WorkflowCardIcon icon={entry.icon} label={entry.name || entry.slug} />
            </span>
            <div className="mkt-hero-text">
              <span className="mkt-hero-eyebrow">
                {entry.authorType === 'official' ? 'Official agent' : 'Promoted agent'}
              </span>
              <h3 className="mkt-hero-title">{entry.name || entry.slug}</h3>
              {tagline && <p className="mkt-hero-subtitle">{tagline}</p>}
              <div className="mkt-hero-actions">
                <button
                  type="button"
                  className="mkt-hero-cta"
                  onClick={() => onSelect(entry)}
                  aria-label={`Deploy ${entry.name || entry.slug}`}
                >
                  <IonIcon icon={rocketOutline} aria-hidden="true" />
                  Deploy
                </button>
              </div>
            </div>
          </div>

          <div className="mkt-hero-right">
            {FEATURED_GRAPHS[entry.slug] ? (
              <FeaturedGraphDemo palette={palette} {...FEATURED_GRAPHS[entry.slug]} />
            ) : SLACK_DEMO_SLUGS.has(entry.slug) ? (
              <FeaturedSlackWindow demos={demos} palette={palette} />
            ) : (
              <FeaturedGenericDemo demo={demos[0]} />
            )}
          </div>
        </div>

        {multi && (
          <>
            {/* No prev/next arrows — pagination dots (bottom-left, over the card)
                are the only control; auto-rotate handles the rest. */}
            <div className="mkt-hero-dots" role="tablist" aria-label="Featured agents">
              {items.map((it, i) => (
                <button
                  key={it.slug}
                  type="button"
                  role="tab"
                  aria-selected={i === index}
                  aria-label={`Show ${it.name || it.slug}`}
                  className={`mkt-hero-dot${i === index ? ' is-active' : ''}`}
                  onClick={() => setIndex(i)}
                />
              ))}
            </div>
          </>
        )}
      </div>
    </div>
  );
};

const MarketplacePage = () => {
  const { projectId } = useParams();
  const navigate = useNavigate();
  const queryClient = useQueryClient();
  const location = useLocation();
  const { project, setToast } = useOutletContext() || {};
  // Hide the App Marketplace section when explicitly on the agents-only
  // marketplace surface. `/marketplace/agents` is canonical; the legacy
  // `/marketplace/workflows` alias still resolves, so match both.
  const workflowsOnly = location.pathname.endsWith('/marketplace/agents')
    || location.pathname === '/marketplace/agents'
    || location.pathname.endsWith('/marketplace/workflows')
    || location.pathname === '/marketplace/workflows';

  // Trending catalog via React Query — cached within-session (staleTime 2min)
  // so navigating back to /marketplace is instant + flash-free; a read-only
  // catalog, so no invalidation/sync concern. `refetch` (aliased fetchTrending)
  // backs the retry button.
  const {
    data: trending,
    isLoading: loading,
    error: trendingError,
    refetch: fetchTrending,
  } = useMarketplace(24);
  const items = useMemo(
    () => (Array.isArray(trending?.items) ? trending.items : []),
    [trending],
  );
  // Ordered list of promoted catalog rows for the top "Featured" hero section.
  // Driven by a dynamic DDB config item (CONFIG#FEATURED) the backend resolves
  // and returns alongside the catalog — changeable WITHOUT a code deploy.
  const featured = useMemo(
    () => (Array.isArray(trending?.featured) ? trending.featured : []),
    [trending],
  );
  const error = trendingError ? (trendingError.message || 'Failed to load marketplace') : null;
  const [activeSlug, setActiveSlug] = useState(null);
  const [activeEntry, setActiveEntry] = useState(null);
  const [deploying, setDeploying] = useState(false);
  // One-Copilot-per-account UX gate. When the user opens a Copilot
  // (chat-runtime) template AND the account already has one deployed, we grey
  // out the Deploy button + explain why. The backend deploy guard is the real
  // enforcement (returns 409 COPILOT_LIMIT_REACHED) — this just stops the click
  // from dead-ending. null = not yet checked / not a Copilot template.
  const [accountHasCopilot, setAccountHasCopilot] = useState(null);
  // BYOK gate — set when a deploy 428s because the target project has no AI
  // credential for the resolved agent. Instead of inventing UI, we open the
  // SHARED AgentModelModal (the same agent+model+key component Project
  // Settings, the pipeline editor, and apps use). Its onSave hands the key
  // back; we re-run the deploy with it as `agentToken` (snapshotted onto the
  // workflow row). Holds { entry, projectId, opts, aiAgent }.
  const [keyModal, setKeyModal] = useState(null);
  const { options: agentOptions } = useAgentOptions();

  // ── Search + facet filter state ───────────────────────────────────
  // searchQuery matches against title / description / tagline / tags
  // (case-insensitive substring). activeFacet is the chip row
  // selection; null === "All" (the default). A "facet" is either a
  // category (Engineering, Testing, …) or a tag (sentry, slack, …) —
  // the user wants vendor names in the chip row, so we union the two
  // sources instead of using categories alone.
  const [searchQuery, setSearchQuery] = useState('');
  const [activeFacet, setActiveFacet] = useState(null);
  // Keys of category groups the user expanded via "See more" — those
  // render every item instead of the top N. The browse surface is the
  // GPT-store-style grouped sections: the chip row SCROLLS to a group
  // (it doesn't filter), "See more" expands a group in place, and
  // typing a search drops into a flat results list.
  const [expandedKeys, setExpandedKeys] = useState([]);
  // searchExpanded drives the collapsed-icon ↔ expanded-input toggle.
  // Stays open whenever there's a query (so collapsing back wouldn't
  // hide what the user already typed); collapses on blur if empty.
  const [searchExpanded, setSearchExpanded] = useState(false);
  const searchInputRef = React.useRef(null);
  const expandSearch = useCallback(() => {
    setSearchExpanded(true);
    // setTimeout so the input is mounted by the time focus() fires.
    setTimeout(() => searchInputRef.current?.focus(), 0);
  }, []);
  const collapseSearchIfEmpty = useCallback(() => {
    if (!searchQuery) setSearchExpanded(false);
  }, [searchQuery]);

  // ── Chip row drag-scroll + right-arrow overflow indicator ─────────
  // Native horizontal overflow scroll works for trackpad swipes but
  // mouse users with only a vertical wheel are stranded — they can
  // see chips overflowing right but have no way to reach them. Two
  // affordances cover both:
  //   1. Mouse-drag scroll: pointerdown → track delta → scrollLeft.
  //      Suppress the synthesized click when the drag traveled >4px
  //      so clicking-and-dragging on a chip doesn't toggle it.
  //   2. Right-edge arrow button + gradient fade: appears whenever
  //      scrollLeft + clientWidth < scrollWidth, hides when scrolled
  //      to the end. Click → scroll by ~viewport-quarter (smooth).
  //
  // The useEffect that re-binds on facet count changes lives further
  // down — AFTER `facets` is declared — to avoid a TDZ error reading
  // facets.length from a hook scheduled above its declaration.
  const chipsRef = React.useRef(null);
  const dragStateRef = React.useRef({ dragging: false, startX: 0, startScroll: 0, moved: false });
  const [chipsRightOverflow, setChipsRightOverflow] = useState(false);

  const updateChipsOverflow = useCallback(() => {
    const el = chipsRef.current;
    if (!el) return;
    // 4px slop so the arrow doesn't flicker when scroll is "basically
    // at the end" but native subpixel rounding leaves a few pixels.
    setChipsRightOverflow(el.scrollLeft + el.clientWidth < el.scrollWidth - 4);
  }, []);

  const onChipsMouseDown = useCallback((e) => {
    // Only left-button starts a drag. Don't interfere with right-click
    // context menu or middle-click scroll-wheel emulation.
    if (e.button !== 0) return;
    const el = chipsRef.current;
    if (!el) return;
    dragStateRef.current = {
      dragging: true,
      startX: e.pageX,
      startScroll: el.scrollLeft,
      moved: false,
    };
    // DOM-level class toggle so the cursor swap doesn't force a React
    // re-render on every mousedown/up (cheap + matches CSS hook).
    el.classList.add('is-dragging');
  }, []);

  const onChipsMouseMove = useCallback((e) => {
    const s = dragStateRef.current;
    if (!s.dragging) return;
    const dx = e.pageX - s.startX;
    if (Math.abs(dx) > 4) s.moved = true;
    if (chipsRef.current) chipsRef.current.scrollLeft = s.startScroll - dx;
  }, []);

  const onChipsMouseUp = useCallback(() => {
    // Leave .moved set briefly so the synthesized click event from a
    // drag-release on a chip can detect "we just dragged" and bail
    // out before toggling the filter. A microtask is enough; the
    // browser dispatches click immediately after mouseup.
    const s = dragStateRef.current;
    s.dragging = false;
    if (chipsRef.current) chipsRef.current.classList.remove('is-dragging');
    setTimeout(() => { s.moved = false; }, 0);
  }, []);

  // Chip click handler that swallows the click if the user just
  // finished a drag-scroll. Returns a new onClick wrapping the real
  // handler so each chip declares its filter behavior once.
  const guardChipClick = useCallback((handler) => (e) => {
    if (dragStateRef.current.moved) {
      e.preventDefault();
      return;
    }
    handler();
  }, []);

  const scrollChipsRight = useCallback(() => {
    const el = chipsRef.current;
    if (!el) return;
    // 60% of the visible width gives the user enough movement to feel
    // meaningful but leaves a couple chips of overlap so they don't
    // lose place visually.
    el.scrollBy({ left: Math.max(160, el.clientWidth * 0.6), behavior: 'smooth' });
  }, []);

  // ── v2 (authed, no project) wizard ─────────────────────────────────
  // Public /marketplace + authed user → detail modal renders v1/v2 with
  // a single full-width Deploy. Clicking Deploy closes the detail modal
  // and opens MarketplaceDeployWizardModal, which is the actual flow:
  // pick (or create) a project + name the workflow + deploy. The wizard
  // owns its own state; this page just lazy-fetches the project list and
  // exposes async create/deploy callbacks.
  const [wizardOpen, setWizardOpen] = useState(false);
  const [wizardEntry, setWizardEntry] = useState(null);
  const [wizardProjects, setWizardProjects] = useState([]);
  const [wizardLoading, setWizardLoading] = useState(false);
  const [creatingProject, setCreatingProject] = useState(false);

  // Trending is fetched + cached by useMarketplace() above (React Query handles
  // mount-fetch, loading/error, and the staleTime cache). Pagination ("load
  // more") stays a one-cursor follow-up; the API supports a limit.

  // ── modal open/close ──────────────────────────────────────────────
  const openDetail = async (entry) => {
    setActiveSlug(entry.slug);
    setActiveEntry(entry); // optimistic — show fields we already have
    // One-Copilot-per-account UX gate: only spend the projects/workflows
    // fan-out when the opened template is actually a Copilot (chat-runtime).
    // Authed users only — anonymous browsers have no account to check (the
    // backend still guards on deploy). Reset to null first so a non-Copilot
    // template never inherits a previous Copilot's "blocked" state.
    setAccountHasCopilot(null);
    if (isChatRuntime(entry) && AuthService.isAuthenticated()) {
      Marketplace.hasAccountCopilot()
        .then((has) => setAccountHasCopilot(!!has))
        .catch(() => setAccountHasCopilot(false));
    }
    // Refresh detail in the background so the modal gets the latest
    // ratings histogram + install count.
    try {
      const fresh = await Marketplace.getWorkflow(entry.slug);
      setActiveEntry(fresh);
    } catch (err) {
      // Non-fatal — modal stays open with the cached row.
      console.warn('Failed to fetch fresh detail for', entry.slug, err);
    }
  };

  const closeDetail = () => {
    setActiveSlug(null);
    setActiveEntry(null);
    setAccountHasCopilot(null);
  };

  // Lazy-load the project list when the wizard opens. First open pays
  // the round-trip; the cached list survives wizard close/re-open until
  // the page unmounts.
  const ensureWizardProjects = useCallback(async () => {
    if (wizardProjects.length > 0) return;
    setWizardLoading(true);
    try {
      const res = await ProjectsService.getProjects();
      setWizardProjects(res?.projects || []);
    } catch (err) {
      console.error('Failed to load projects for wizard:', err);
      setWizardProjects([]);
    } finally {
      setWizardLoading(false);
    }
  }, [wizardProjects.length]);

  // Shared deploy primitive — same backend call regardless of who's
  // invoking it (v3 directly, or v2 via the wizard). Surfaces a toast,
  // navigates to the new workflow's detail page on success.
  const runDeploy = useCallback(async (entry, targetProjectId, opts = {}) => {
    // ALWAYS confirm the credential in the shared modal first — even when the
    // project already has a key (pre-filled masked) — so the user sees + can
    // override which token/model this deploy will use. The modal re-enters
    // here with _fromKeyModal once confirmed. Only for in-project (v3) deploys
    // where we hold the project's masked secrets; the wizard path falls
    // through to the 428-driven prompt.
    if (!opts._fromKeyModal && project && project.projectId === targetProjectId) {
      setActiveSlug(null);
      setActiveEntry(null);
      setWizardOpen(false);
      setKeyModal({
        entry,
        projectId: targetProjectId,
        opts,
        aiAgent: opts.aiAgent || project.aiAgent,
        aiModel: opts.aiModel || project.aiModel,
        seedKey: project.secrets?.aiApiKey || '',
        seedOAuth: project.secrets?.aiOAuthToken || '',
      });
      return;
    }
    const deployOpts = {};
    if (typeof opts.warm === 'number') deployOpts.warm = opts.warm;
    if (typeof opts.workflowName === 'string' && opts.workflowName.trim()) {
      deployOpts.workflowName = opts.workflowName.trim();
    }
    // Forward the agent + model override picked in the deploy footer.
    // Empty / unset = "project default" — keep it out of the POST so
    // workflow row's aiAgent / aiModel stay absent and runtime inherits.
    if (typeof opts.aiAgent === 'string' && opts.aiAgent) {
      deployOpts.aiAgent = opts.aiAgent;
    }
    if (typeof opts.aiModel === 'string' && opts.aiModel) {
      deployOpts.aiModel = opts.aiModel;
    }
    // Per-agent integration allowlist (least-privilege). Present only when the
    // user deselected at least one connected integration in the modal; absent
    // = "all connected allowed" (the service drops empty/missing arrays).
    if (Array.isArray(opts.enabledIntegrations)) {
      deployOpts.enabledIntegrations = opts.enabledIntegrations;
    }
    // Adaptive deploy-time inputs (registry-driven, from the template's
    // inputSchema deployInput annotations). An object like
    //   { repoUrl: 'https://github.com/acme/web' }
    // collected by MarketplaceDetailModal's "Required inputs" section.
    // Derived fields (e.g. circleProjectSlug) are intentionally ABSENT —
    // the backend resolves them from their deriveFrom source at deploy.
    // Present only when the template declared deploy inputs AND the user
    // entered at least one (modal sends undefined otherwise) → deploy body
    // stays unchanged for templates with no deploy inputs.
    if (opts.defaultInput && typeof opts.defaultInput === 'object' && !Array.isArray(opts.defaultInput)) {
      deployOpts.defaultInput = opts.defaultInput;
    }
    // BYOK: an ad-hoc key collected inline when the project has none, plus
    // the opt-in "set as project default" flag. Only present on a retry
    // after the first deploy returned 428 AGENT_KEY_MISSING.
    if (typeof opts.agentToken === 'string' && opts.agentToken) {
      deployOpts.agentToken = opts.agentToken;
      if (opts.saveToProject) deployOpts.saveToProject = true;
    }
    setDeploying(true);
    try {
      const res = await Marketplace.deployWorkflow(entry.slug, targetProjectId, deployOpts);
      const projName = opts.projectName || project?.name || 'this project';
      const warmSuffix = typeof opts.warm === 'number' && opts.warm > 0
        ? ` with ${opts.warm} warm worker${opts.warm === 1 ? '' : 's'}`
        : '';
      const deployedAs = res?.workflowType || deployOpts.workflowName || entry.slug;
      if (res?.alreadyInstalled) {
        setToast?.({
          isOpen: true,
          message: `${entry.name || entry.slug} is already deployed to ${projName}${warmSuffix}`,
          color: 'warning',
        });
      } else {
        setToast?.({
          isOpen: true,
          message: `Deployed ${entry.name || entry.slug} to ${projName} as ${deployedAs}${warmSuffix}`,
          color: 'success',
        });
      }
      closeDetail();
      setWizardOpen(false);
      // Drop the stale Agents-list + sidebar caches so the just-deployed agent
      // shows up immediately (instead of waiting out the list's stale window).
      queryClient.invalidateQueries({ queryKey: queryKeys.agents.list(targetProjectId) });
      queryClient.invalidateQueries({ queryKey: ['workspace', 'sidebar'] });
      // CRITICAL: also drop the DETAIL cache for this workflowType. If the user
      // opened /agents/<type> BEFORE deploying, the backend returned a
      // `notInstalled` 404-shell that useAgent cached (5min) — without this, the
      // post-deploy navigate re-renders that stale "isn't deployed here yet"
      // page instead of the freshly-deployed agent.
      queryClient.invalidateQueries({ queryKey: queryKeys.agents.detail(targetProjectId, deployedAs) });
      navigate(`/projects/${targetProjectId}/agents/${encodeURIComponent(deployedAs)}`);
    } catch (err) {
      console.error('Deploy failed:', err);
      // The api service attaches the HTTP status as `err.status` and the
      // parsed JSON payload as `err.body` (NOT err.statusCode / err.data).
      const status = err?.status || err?.statusCode;
      const body = err?.body || err?.data || {};
      const code = body.code || err?.code;
      // BYOK gate — the project has no AI credential for the resolved agent.
      // Open the SHARED AgentModelModal (don't invent UI / don't dead-end on
      // a "go to settings" dialog). Its onSave re-runs this deploy with the
      // captured key as `agentToken`. Skip when we already sent a token (the
      // retry from that modal) — fall through to throw so the modal shows the
      // error inline rather than re-opening.
      if (status === 428 && code === 'AGENT_KEY_MISSING' && !deployOpts.agentToken) {
        setActiveSlug(null);
        setActiveEntry(null);
        setWizardOpen(false);
        setKeyModal({ entry, projectId: targetProjectId, opts, aiAgent: body.aiAgent });
        return;
      }
      // Bad ad-hoc key — let the footer show the error against its field.
      if (status === 400 && code === 'AGENT_TOKEN_INVALID') {
        const e = new Error(body.error || err?.message || 'That key looks invalid.');
        e.code = 'AGENT_TOKEN_INVALID';
        throw e;
      }
      const suggestion = body.suggestion || err?.suggestion;
      const detail = status === 409 && suggestion
        ? `${err.message} — try "${suggestion}"`
        : (err.message || 'unknown error');
      setToast?.({
        isOpen: true,
        message: `Deploy failed: ${detail}`,
        color: 'danger',
      });
    } finally {
      setDeploying(false);
    }
  }, [navigate, project, setToast, queryClient]);

  // ── deploy ────────────────────────────────────────────────────────
  // opts.warm — integer 1..5 to provision warm Fargate tasks for the
  // deployed workflow (paid feature, ~60s provisioning, eliminates the
  // cold-start delay on trigger). Undefined = cold-start default.
  // opts.workflowName — the user's chosen name (v3 only).
  //
  // Three flavours of click, dispatched by URL projectId + auth state:
  //   v1 (unauth, no project) → bounce to /connect, returnTo=/marketplace
  //   v2 (authed, no project) → close detail modal, open wizard. Wizard
  //                              owns pick/create and triggers runDeploy.
  //   v3 (project in scope)   → POST /deploy directly, navigate on success
  // The backend deploy contract is unchanged across paths.
  const handleDeploy = async (entry, opts = {}) => {
    if (!entry?.slug) return;

    if (!projectId) {
      if (!AuthService.isAuthenticated()) {
        // v1 → after login, return to /marketplace as v2.
        navigate(`/connect?returnTo=${encodeURIComponent('/marketplace')}`);
        return;
      }
      // v2 → close the detail modal and open the wizard. Wizard owns
      // pick/create + name + deploy from this point.
      setWizardEntry(entry);
      closeDetail();
      setWizardOpen(true);
      ensureWizardProjects();
      return;
    }

    // v3 → deploy directly into the URL-scoped project. Return the promise
    // so the footer can await it (inline key-entry on 428).
    return runDeploy(entry, projectId, opts);
  };

  // Wizard → user clicked Create. POSTs the new project, returns it so
  // the wizard can select it. Errors surface as toasts; wizard stays
  // open so the user can retry without losing context.
  const handleWizardCreateProject = useCallback(async ({ name }) => {
    setCreatingProject(true);
    try {
      const res = await ProjectsService.createProject({ name });
      const created = res?.project;
      if (created?.projectId) {
        setWizardProjects((prev) => [...prev, created]);
        setToast?.({
          isOpen: true,
          message: `Created project "${created.name}"`,
          color: 'success',
        });
        return created;
      }
      return null;
    } catch (err) {
      console.error('Create project failed:', err);
      setToast?.({
        isOpen: true,
        message: `Couldn't create project: ${err?.message || 'unknown error'}`,
        color: 'danger',
      });
      throw err;
    } finally {
      setCreatingProject(false);
    }
  }, [setToast]);

  // Wizard → user clicked Deploy with a chosen project + name. Forward the
  // FULL opts (name, agent/model, warm, BYOK agentToken/saveToProject) — not
  // just name — so the wizard path matches the in-project deploy. Return the
  // promise so the footer can await it (inline key-entry on 428).
  const handleWizardDeploy = useCallback((opts = {}) => {
    const targetId = opts.projectId;
    if (!wizardEntry?.slug || !targetId) return undefined;
    return runDeploy(wizardEntry, targetId, opts);
  }, [wizardEntry, runDeploy]);

  // When mounted at the public top-level /marketplace, projectId is
  // undefined → render with the marketing-site chrome (PublicNav +
  // PublicFooter) so it's not a blank dark page. Inside a project
  // (/projects/:id/marketplace) ProjectLayout already supplies the
  // sidebar + header, so we render the bare content.
  const isPublic = !projectId;

  // ── Filter chips: union of categories + tags across loaded items ─
  // The chip row mixes two facet sources because users think in BOTH
  // dimensions: "show me Testing workflows" (category) and "show me
  // anything that touches Sentry" (tag/vendor). Treating them
  // uniformly lets one chip row serve both.
  //
  // Sorted by frequency descending so the most common facets surface
  // first; alpha tiebreak for stable display order between rebuilds.
  // The "child-workflow" meta-tag is filtered out — it's an internal
  // architectural marker (notify-slack, notify-lark) not a user-facing
  // browse axis.
  const facets = React.useMemo(() => {
    // Tag-pool model (2026-05-22): tags are CURATED via
    // packages/workflow-templates/tag-pool.js. The chip row renders
    // pool entries in pool order, NOT alphabetical or frequency-
    // ranked — pool order encodes editorial priority (most-common
    // jobs-to-be-done first).
    //
    // Anything not in the pool — legacy DDB rows, the child-workflow
    // meta-tag, or category fields lingering on old rows — is
    // intentionally DROPPED from the chip row. The pool IS the
    // marketplace browse vocabulary.
    const counts = new Map();
    for (const it of items) {
      for (const t of (Array.isArray(it.tags) ? it.tags : [])) {
        const tag = String(t).trim();
        if (!tag || tag === 'child-workflow') continue;
        if (!MARKETPLACE_TAG_SET.has(tag)) continue; // drop non-pool tags
        counts.set(tag, (counts.get(tag) || 0) + 1);
      }
    }
    // Render in pool order, only including tags with at least one
    // workflow currently using them (so the row doesn't show empty
    // chips when a pool entry has no template yet).
    return MARKETPLACE_TAG_POOL
      .filter((tag) => (counts.get(tag) || 0) > 0)
      .map((tag) => ({ name: tag, count: counts.get(tag), label: formatFacetLabel(tag) }));
  }, [items]);

  // Chip-row overflow-listener effect — has to live AFTER `facets` so
  // the deps array can read facets.length without a TDZ violation.
  // The handlers themselves (above) only close over refs, so they're
  // fine declared earlier.
  //
  // Also attaches a non-passive wheel listener so vertical wheel
  // motion (when the user hovers over the chip row) translates to
  // horizontal scroll inside the row instead of scrolling the whole
  // page. We need a NATIVE listener with {passive: false} because
  // React's synthetic onWheel is registered as passive by default —
  // preventDefault() inside a synthetic handler is a no-op.
  useEffect(() => {
    const el = chipsRef.current;
    if (!el) return;
    updateChipsOverflow();
    el.addEventListener('scroll', updateChipsOverflow, { passive: true });
    let ro;
    if (typeof ResizeObserver !== 'undefined') {
      ro = new ResizeObserver(updateChipsOverflow);
      ro.observe(el);
    }
    // Vertical-wheel → horizontal scroll bridge. We take the wheel
    // delta with the larger magnitude (deltaY for mouse wheels, deltaX
    // for trackpad horizontal swipes that some hardware emits) so
    // trackpad users with native horizontal momentum keep working.
    // No-op when the row isn't overflowing — never steal scroll the
    // user wasn't expecting us to handle.
    const onWheel = (e) => {
      if (el.scrollWidth <= el.clientWidth) return;
      const useY = Math.abs(e.deltaY) > Math.abs(e.deltaX);
      const delta = useY ? e.deltaY : e.deltaX;
      if (delta === 0) return;
      el.scrollLeft += delta;
      e.preventDefault();
    };
    el.addEventListener('wheel', onWheel, { passive: false });
    return () => {
      el.removeEventListener('scroll', updateChipsOverflow);
      el.removeEventListener('wheel', onWheel);
      if (ro) ro.disconnect();
    };
  }, [updateChipsOverflow, facets.length]);

  // ── Visible items: apply search + facet filter ────────────────────
  // Facet matching is OR across category + tags — the user picked one
  // chip and either a category-equal hit or a tag-includes hit wins.
  // Search is a separate case-insensitive substring across the fields
  // a human types: name, displayName, tagline, description, tags.
  const filteredItems = React.useMemo(() => {
    const q = searchQuery.trim().toLowerCase();
    return items.filter((entry) => {
      if (activeFacet) {
        const tags = Array.isArray(entry.tags) ? entry.tags : [];
        const matchesFacet = entry.category === activeFacet || tags.includes(activeFacet);
        if (!matchesFacet) return false;
      }
      if (!q) return true;
      const haystack = [
        entry.name,
        entry.displayName,
        entry.tagline,
        entry.description,
        ...(Array.isArray(entry.tags) ? entry.tags : []),
      ].filter(Boolean).join(' ').toLowerCase();
      return haystack.includes(q);
    });
  }, [items, searchQuery, activeFacet]);

  // ── Grouped browse (GPT-store layout) ─────────────────────────────
  // Default surface = a "Trending" section followed by one section per
  // tag (pool order), each showing the top N with a "See more" that
  // drops into the flat filtered list. Only the default state
  // (no facet, no search, not expanded) renders grouped.
  const GROUP_SIZE = 6;
  // Grouped unless the user is searching — search results render as a
  // single flat list. The chip row never filters; it scrolls.
  const showGrouped = !searchQuery.trim();
  const groups = React.useMemo(() => {
    if (!showGrouped) return [];
    const out = [];
    // Trending section hidden — deploy counts aren't real yet, and it
    // duplicated items that also appear in the category sections below.
    // Restore by re-pushing a { key:'__trending__', label:'Trending',
    // subtitle:'Most-deployed pipelines right now', all: items.slice(0,12) }
    // group here once deploy-rank data is real.
    //
    // One section per pool tag that has workflows, in pool order. Each
    // workflow appears in EXACTLY ONE section — the FIRST tag (in editorial
    // pool order) it matches, i.e. its "primary" category. `seen` tracks
    // already-placed slugs so a multi-tag item (e.g. tags
    // [reports, ai-spend, cost-tracking]) no longer repeats across every
    // matching section.
    const seen = new Set();
    // NOTE: featured items are NOT excluded here. The hero carousel at the top
    // is a PROMOTIONAL surface only — a featured agent (e.g. Sentry Triage,
    // GitHub/GitLab Code Review) must STILL appear in its normal category
    // section below so it's discoverable by category, not just via the slider.
    for (const facet of facets) {
      const inFacet = items.filter(
        (it) =>
          Array.isArray(it.tags) &&
          it.tags.includes(facet.name) &&
          !seen.has(it.slug)
      );
      if (inFacet.length === 0) continue;
      // Rating-first within the tag: highest average, then most ratings, then
      // name. (The backend returns a global trending order; we re-sort the
      // per-tag slice so high-rated templates surface at the top of each tag.)
      inFacet.sort((a, b) =>
        ((Number(b.ratingAvg) || 0) - (Number(a.ratingAvg) || 0))
        || ((Number(b.ratingCount) || 0) - (Number(a.ratingCount) || 0))
        || String(a.slug || '').localeCompare(String(b.slug || '')));
      inFacet.forEach((it) => seen.add(it.slug));
      out.push({
        key: facet.name,
        label: facet.label,
        subtitle: null,
        all: inFacet,
      });
    }
    return out;
  }, [showGrouped, items, facets]);

  // Smooth-scroll the chip row's target group into view (chips are
  // anchors, not filters). Group containers carry id="mkt-group-<key>".
  const scrollToGroup = useCallback((key) => {
    if (typeof document === 'undefined') return;
    const el = document.getElementById(`mkt-group-${key}`);
    if (el) el.scrollIntoView({ behavior: 'smooth', block: 'start' });
  }, []);

  // "See more" expands one group in place (render all its items).
  const expandGroup = useCallback((key) => {
    setExpandedKeys((prev) => (prev.includes(key) ? prev : [...prev, key]));
  }, []);

  // Card renderer shared by the flat grid and the grouped sections so
  // both stay visually identical. idx drives the rank badge.
  const renderCard = (entry, idx) => (
    <li key={entry.slug} className="marketplace-card-li">
      <button
        type="button"
        className="marketplace-card"
        onClick={() => openDetail(entry)}
        aria-label={`${entry.name} by ${entry.authorName}`}
      >
        <span className="marketplace-rank">{idx + 1}</span>
        <span className="marketplace-card-icon-wrap">
          <WorkflowCardIcon icon={entry.icon} label={entry.name || entry.slug} />
        </span>
        <span className="marketplace-card-body">
          <span className="marketplace-card-name">{entry.name || entry.slug}</span>
          <span className="marketplace-card-tagline">
            {(entry.description || entry.tagline || '').slice(0, MAX_TAGLINE)}
          </span>
          <span className="marketplace-card-author">
            By {entry.authorName || 'Unknown'}
            {entry.authorType === 'official' && (
              <span className="marketplace-card-author-badge">Official</span>
            )}
            {Number(entry.ratingCount) > 0 && (
              <span className="marketplace-card-rating" aria-label={`${Number(entry.ratingAvg).toFixed(1)} stars, ${entry.ratingCount} rating${entry.ratingCount === 1 ? '' : 's'}`}>
                <IonIcon icon={star} aria-hidden="true" />
                <span className="marketplace-card-rating-value">
                  {Number(entry.ratingAvg).toFixed(1)}
                </span>
                <span className="marketplace-card-rating-count">
                  ({entry.ratingCount})
                </span>
              </span>
            )}
          </span>
        </span>
      </button>
    </li>
  );

  const content = (
    <>
      <div className="marketplace-page">
        <header className="marketplace-header">
          <div className="marketplace-title-row">
            <div className="marketplace-title-left">
              <h1 className="marketplace-title">Agent Marketplace</h1>
              <p className="marketplace-subtitle">
                Discover and deploy agents shared by Zibby and the community.
              </p>
            </div>
            {/* Search — collapsed to a single icon button by default;
                clicking expands it inline into a pill-shaped input.
                Stays expanded while the query is non-empty (so a
                stray blur doesn't hide what the user already typed).
                On narrow screens the whole control still wraps below
                the title via the parent's flex-wrap. */}
            <div
              className={`marketplace-search-wrap${searchExpanded || searchQuery ? ' is-expanded' : ''}`}
              role="search"
            >
              {searchExpanded || searchQuery ? (
                <>
                  <IonIcon icon={searchOutline} className="marketplace-search-icon" aria-hidden="true" />
                  <input
                    ref={searchInputRef}
                    type="search"
                    className="marketplace-search-input"
                    placeholder="Search agents…"
                    aria-label="Search marketplace agents"
                    value={searchQuery}
                    onChange={(e) => setSearchQuery(e.target.value)}
                    onBlur={collapseSearchIfEmpty}
                    onKeyDown={(e) => {
                      if (e.key === 'Escape') {
                        setSearchQuery('');
                        setSearchExpanded(false);
                      }
                    }}
                  />
                  {searchQuery && (
                    <button
                      type="button"
                      className="marketplace-search-clear"
                      aria-label="Clear search"
                      // Mousedown (not click) so we beat the input's
                      // blur — without this, clicking the X causes
                      // collapseSearchIfEmpty to fire first with the
                      // current query, then setSearchQuery('') happens
                      // and the input is gone (intended), but it's
                      // clearer to handle the order explicitly.
                      onMouseDown={(e) => e.preventDefault()}
                      onClick={() => {
                        setSearchQuery('');
                        setSearchExpanded(false);
                      }}
                    >
                      <IonIcon icon={closeCircle} aria-hidden="true" />
                    </button>
                  )}
                </>
              ) : (
                <button
                  type="button"
                  className="marketplace-search-toggle"
                  aria-label="Open search"
                  onClick={expandSearch}
                >
                  <IonIcon icon={searchOutline} aria-hidden="true" />
                </button>
              )}
            </div>
          </div>

          {/* Facet chip row — union of categories + tags so users can
              filter by either "Engineering" or "Sentry" / "Slack" in one
              row. Hidden while loading + when fewer than 2 facets exist
              (single-axis filter would be a no-op). Horizontal scroll
              on overflow + mouse-drag + right-edge arrow with fade
              shade so users can always discover off-screen chips. */}
          {!loading && facets.length > 1 && (
            <div className="marketplace-chips-wrap">
              <nav
                ref={chipsRef}
                className="marketplace-chips"
                aria-label="Filter agents"
                onMouseDown={onChipsMouseDown}
                onMouseMove={onChipsMouseMove}
                onMouseUp={onChipsMouseUp}
                onMouseLeave={onChipsMouseUp}
              >
                <button
                  type="button"
                  className={`marketplace-chip${activeFacet === null ? ' is-active' : ''}`}
                  onClick={guardChipClick(() => scrollToGroup('__trending__'))}
                  aria-pressed={activeFacet === null}
                >
                  All
                  <span className="marketplace-chip-count">{items.length}</span>
                </button>
                {facets.map((facet) => {
                  const isActive = activeFacet === facet.name;
                  return (
                    <button
                      key={facet.name}
                      type="button"
                      className={`marketplace-chip${isActive ? ' is-active' : ''}`}
                      onClick={guardChipClick(() => scrollToGroup(facet.name))}
                      aria-pressed={isActive}
                    >
                      {facet.label}
                      <span className="marketplace-chip-count">{facet.count}</span>
                    </button>
                  );
                })}
              </nav>
              {chipsRightOverflow && (
                <button
                  type="button"
                  className="marketplace-chips-arrow"
                  aria-label="Scroll filters right"
                  onClick={scrollChipsRight}
                >
                  <IonIcon icon={chevronForwardOutline} aria-hidden="true" />
                </button>
              )}
            </div>
          )}
        </header>

        <section className="marketplace-section" aria-label="Marketplace workflows">
          {loading ? (
            <div className="marketplace-grid">
              {Array.from({ length: 6 }).map((_, i) => (
                <div key={i} className="marketplace-card marketplace-card--skeleton" aria-hidden="true">
                  <span className="marketplace-rank">{i + 1}</span>
                  <div className="marketplace-card-skeleton-icon" />
                  <div className="marketplace-card-skeleton-text" />
                </div>
              ))}
            </div>
          ) : error ? (
            <div className="marketplace-error" role="alert">
              <p>Couldn't load the marketplace.</p>
              <button type="button" className="marketplace-retry" onClick={() => fetchTrending()}>
                Retry
              </button>
            </div>
          ) : items.length === 0 ? (
            <div className="marketplace-empty">
              <p>No agents in the marketplace yet — check back soon.</p>
            </div>
          ) : filteredItems.length === 0 ? (
            <div className="marketplace-empty">
              <p>
                No agents match
                {searchQuery && (
                  <> &ldquo;<strong>{searchQuery}</strong>&rdquo;</>
                )}
                {activeFacet && (
                  <> in <strong>{formatFacetLabel(activeFacet)}</strong></>
                )}
                .
              </p>
              <button
                type="button"
                className="marketplace-retry"
                onClick={() => { setSearchQuery(''); setActiveFacet(null); }}
              >
                Clear filters
              </button>
            </div>
          ) : showGrouped ? (
            <div className="marketplace-groups">
              {/* Featured / promoted hero row — pinned at the very TOP, above
                  every category section. Driven by the dynamic CONFIG#FEATURED
                  DDB item (changeable without a deploy). Promoted agents ALSO
                  still appear in their normal category section below (like ads). */}
              {featured.length > 0 && (
                <FeaturedHeroCarousel items={featured} onSelect={openDetail} />
              )}
              {groups.map((g) => {
                const expanded = expandedKeys.includes(g.key);
                const shown = expanded ? g.all : g.all.slice(0, GROUP_SIZE);
                const hasMore = g.all.length > GROUP_SIZE;
                return (
                  <div className="mkt-group" id={`mkt-group-${g.key}`} key={g.key}>
                    <div className="mkt-group-head">
                      <div className="mkt-group-head-text">
                        <h2 className="mkt-group-title">{g.label}</h2>
                        {g.subtitle && <p className="mkt-group-subtitle">{g.subtitle}</p>}
                      </div>
                      {hasMore && !expanded && (
                        <button
                          type="button"
                          className="mkt-group-seemore"
                          onClick={() => expandGroup(g.key)}
                        >
                          See all {g.all.length}
                          <IonIcon icon={chevronForwardOutline} aria-hidden="true" />
                        </button>
                      )}
                    </div>
                    <ol className="marketplace-grid">
                      {shown.map((entry, idx) => renderCard(entry, idx))}
                    </ol>
                  </div>
                );
              })}
            </div>
          ) : (
            <ol className="marketplace-grid">
              {filteredItems.map((entry, idx) => renderCard(entry, idx))}
            </ol>
          )}
        </section>

        {/* App Marketplace — hidden on /marketplace/workflows where
            the user explicitly wants workflows only. Shown on the
            combined /marketplace view. */}
        {!workflowsOnly && <AppsMarketplaceSection embedded />}
      </div>

      {activeSlug && (
        <MarketplaceDetailModal
          entry={activeEntry}
          projectId={projectId}
          projectName={project?.name}
          projectAiAgent={project?.aiAgent || ''}
          projectAiModel={project?.aiModel || ''}
          isOpen={!!activeSlug}
          onClose={closeDetail}
          onDeploy={handleDeploy}
          deploying={deploying}
          // One-Copilot-per-account: grey out Deploy when this is a Copilot
          // template AND the account already has one. activeEntry is the
          // freshest row (carries runtime); accountHasCopilot is the result of
          // the fan-out kicked off in openDetail.
          copilotBlocked={isChatRuntime(activeEntry) && accountHasCopilot === true}
          copilotBlockedMessage={COPILOT_LIMIT_MESSAGE}
          onRate={async (entry, n) => {
            try {
              await Marketplace.rateWorkflow(entry.slug, { rating: n });
              // Server already upserts + recomputes histogram. Refetch
              // ONLY the detail so myRating + aggregate in the open
              // modal update. Intentionally NOT refetching the trending
              // list — the user's own click should not shuffle the
              // catalog under them. Other users will see the new rank
              // on their next page load (the score is in
              // installCount*10 + ratingBonus, recomputed server-side).
              const fresh = await Marketplace.getWorkflow(entry.slug);
              setActiveEntry(fresh);
              setToast?.({
                isOpen: true,
                message: `Thanks — rated ${n} star${n === 1 ? '' : 's'}`,
                color: 'success',
              });
            } catch (err) {
              console.error('Rate failed:', err);
              setToast?.({
                isOpen: true,
                message: `Couldn't save rating: ${err.message || 'unknown error'}`,
                color: 'danger',
              });
            }
          }}
        />
      )}

      {/* v2 wizard — opens when an authed user on the public route
          clicks Deploy. Owns pick (or create) project + name workflow +
          deploy. Only rendered on the public route. */}
      {!projectId && (
        <MarketplaceDeployWizardModal
          entry={wizardEntry}
          isOpen={wizardOpen}
          onClose={() => setWizardOpen(false)}
          projects={wizardProjects}
          projectsLoading={wizardLoading}
          onCreateProject={handleWizardCreateProject}
          creatingProject={creatingProject}
          onDeploy={handleWizardDeploy}
          deploying={deploying}
        />
      )}

      {/* Deploy credential confirm — the SHARED AgentModelModal (same component
          the pipeline editor, apps, and Project Settings → AI use). ALWAYS
          shown on deploy: pre-filled with the project's token (masked) when it
          has one so the user confirms "deploy using this", or pastes a new key
          when the project has none. onSave re-enters runDeploy with
          _fromKeyModal so it actually deploys. A new key rides as agentToken;
          keeping the masked project key sends none → backend uses the project
          key and snapshots it onto the agent as an env var. */}
      <AgentModelModal
        isOpen={!!keyModal}
        onClose={() => setKeyModal(null)}
        variant="pipeline"
        title="Deploy — agent, model & key"
        agentOptions={agentOptions}
        submitLabel="Save and deploy"
        seedKey={keyModal?.seedKey || ''}
        seedOAuth={keyModal?.seedOAuth || ''}
        showSaveToProject={!!keyModal && !keyModal.seedKey && !keyModal.seedOAuth}
        seedValue={keyModal
          ? findAgentOptionValue(
            { aiAgent: keyModal.aiAgent, aiModel: keyModal.opts?.aiModel || keyModal.aiModel },
            agentOptions,
          )
          : ''}
        inputSchema={keyModal?.entry?.inputSchema || null}
        onSave={async (bodyVals) => {
          const token = bodyVals.aiApiKey || bodyVals.aiOAuthToken;
          await runDeploy(keyModal.entry, keyModal.projectId, {
            ...keyModal.opts,
            ...(bodyVals.aiAgent ? { aiAgent: bodyVals.aiAgent } : {}),
            ...(bodyVals.aiModel ? { aiModel: bodyVals.aiModel } : {}),
            ...(token ? { agentToken: token } : {}),
            ...(bodyVals.saveToProject ? { saveToProject: true } : {}),
            ...(bodyVals.defaultInput ? { defaultInput: bodyVals.defaultInput } : {}),
            _fromKeyModal: true,
          });
          setKeyModal(null);
        }}
      />
    </>
  );

  return (
    <>
      <Helmet>
        <title>Marketplace · Zibby</title>
      </Helmet>
      {isPublic ? (
        <div className="public-page fade-in marketplace-public-wrap">
          <PublicNav />
          {content}
          <PublicFooter />
        </div>
      ) : (
        content
      )}
    </>
  );
};

export default MarketplacePage;
